import CustomAsyncSelect, {
  PromiseOptionsCallback,
} from "@app/components/custom-async-select/custom-async-select.component";
import PaginationMui from "@app/components/pagination-mui/pagination-mui.component";
import { DEBOUNCE_TIMER, DEFAULT_PAGE, OrderType } from "@app/constants";
import DollarIcon from "@app/icons/dashboard/dollar";
import AuthorService from "@app/services/http/author.service";
import DashboardService from "@app/services/http/dashboard.service";
import { LabelValue, Meta, StatisticsState } from "@app/types";
import {
  AuthorRevenue,
  RequestParamsDashboard,
} from "@app/types/dashboard.type";
import { Images } from "@assets/images";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { useDebouncedCallback } from "@core/hooks/use-debounced-callback.hook";
import clsx from "clsx";
import { memo, useCallback, useEffect, useState } from "react";
import * as XLSX from "xlsx";

const headerTitle = [
  {
    label: "TÊN",
    key: "name",
    hasOrder: true,
  },
  {
    label: "HÌNH ẢNH",
    key: "thumbnails",
    hasOrder: false,
  },
  {
    label: "BÁN LẺ",
    key: "retailRevenue",
    hasOrder: false,
  },
  {
    label: "BÁN LẺ (all)",
    key: "retailRevenueAllTime",
    hasOrder: false,
  },
  {
    label: "VIP",
    key: "vipRevenue",
    hasOrder: false,
  },
  {
    label: "VIP (all)",
    key: "vipRevenueAllTime",
    hasOrder: false,
  },
  {
    label: "ĐÃ TRẢ (rút)",
    key: "paidAmount",
    hasOrder: true,
  },
  {
    label: "CÒN LẠI (rút)",
    key: "remainAmount",
    hasOrder: true,
  },
];

const AuthorPaymentInformation = ({ filter }: { filter: StatisticsState }) => {
  const { abortController } = useCancellationRequest();
  const [data, setData] = useState<AuthorRevenue>();
  const [meta, setMeta] = useState<Meta>();

  const [requestParams, setRequestParams] = useState<RequestParamsDashboard>({
    pageNum: DEFAULT_PAGE,
    pageSize: 20,
  });
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("");

  useEffect(() => {
    DashboardService.getAuthorRevenue({
      ...requestParams,
      startDate: filter.startDate || undefined,
      endDate: filter.endDate || undefined,
    }).then((res) => {
      setData(res.data);
      setMeta(res.data.meta);
    });
  }, [requestParams, filter.startDate, filter.endDate]);

  useEffect(() => {
    const newRequestParams: RequestParamsDashboard = {
      ...requestParams,
      sortBy: orderBy,
      sortType: order as OrderType,
    };

    setRequestParams(newRequestParams);
  }, [orderBy, order]);

  const onSearchChange = useDebouncedCallback((value: number | null) => {
    const newRequestParams: RequestParamsDashboard = {
      ...requestParams,
      ...(value !== null ? { authorId: value } : {}),
      keyword: undefined,
    };

    setRequestParams(newRequestParams);
  }, DEBOUNCE_TIMER);

  const getAuthorsOptions = useCallback(
    (inputValue: string, callback: PromiseOptionsCallback) => {
      AuthorService.getList(abortController.signal, false, {
        keyword: inputValue,
        sortType: "ASC",
      }).then((res) => {
        const data: LabelValue[] = res.data.items;

        callback(data);
      });
    },
    []
  );

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParamsDashboard = {
      ...requestParams,
      pageNum: page,
    };

    setRequestParams(newRequestParams);
  };

  const onPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParamsDashboard = {
      ...requestParams,
      pageSize,
    };

    setRequestParams(newRequestParams);
  };

  const handleExportToExcel = () => {
    if (!data?.items) return;

    const excelData = [
      [
        "Tên",
        "HÌNH ẢNH",
        "BÁN LẺ",
        "BÁN LẺ (all)",
        "VIP",
        "VIP (all)",
        "ĐÃ TRẢ (rút)",
        "CÒN LẠI (rút)",
      ],
      // eslint-disable-next-line no-unsafe-optional-chaining
      ...data?.items.map((item) => [
        item.name,
        item.thumbnails,
        `${item.retailRevenue?.toLocaleString("de-DE") ?? 0}đ`,
        `${item.retailRevenueAllTime?.toLocaleString("de-DE") ?? 0}đ`,
        `${item.vipRevenue?.toLocaleString("de-DE") ?? 0}đ`,
        `${item.vipRevenueAllTime?.toLocaleString("de-DE") ?? 0}đ`,
        `${item.paidAmount?.toLocaleString("de-DE") ?? 0}đ`,
        `${item.remainAmount?.toLocaleString("de-DE") ?? 0}đ`,
      ]),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(excelData);

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Danh sách");

    XLSX.writeFile(workbook, "Danh_sach.xlsx");
  };

  const handleClickHeader = (item: any) => {
    if (item.hasOrder) {
      const orderBy = item.key;
      const orderType =
        order === OrderType.ASC ? OrderType.DESC : OrderType.ASC;

      setOrderBy(orderBy);
      setOrder(orderType);
    }
  };

  return (
    <div className="p-[24px] bg-white rounded-xl flex flex-col gap-[20px]">
      <div className="flex justify-between items-center">
        <div className="flex gap-1 items-center">
          <DollarIcon />
          <p className="text-[18px] text-[#45464E] font-medium">
            Doanh thu author chi tiết
          </p>
        </div>
        <div className="flex gap-2">
          <div className="w-[250px]">
            <CustomAsyncSelect
              promiseOptions={getAuthorsOptions}
              placeholder="Chọn author"
              onChange={(e) => onSearchChange(e ? Number(e.value) : null)}
            />
          </div>
          <button
            type="button"
            disabled={!data?.items?.length}
            className="focus:outline-none text-white bg-green-700 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 disabled:opacity-50"
            onClick={handleExportToExcel}
          >
            Export Excel
          </button>
        </div>
      </div>
      <div className="relative overflow-x-auto">
        <table className="w-full text-left rtl:text-right">
          <thead>
            <tr className="border-t border-[#EEF0FA]">
              {headerTitle?.map((it) => (
                <th
                  key={it.key}
                  className={clsx(
                    "px-6 py-3 text-[14px] text-[#6E7079] font-normal cursor-pointer",
                    {
                      "bg-[#bac5d4]": it.key === orderBy,
                    }
                  )}
                  onClick={() => handleClickHeader(it)}
                >
                  <div className={"flex items-center"}>
                    {it.label}

                    {it.hasOrder && (!order || it.key !== orderBy) && (
                      <span className="ml-2">
                        <img
                          src={Images.SortIcon.default}
                          alt="sort-icon"
                          className="w-4"
                        />
                      </span>
                    )}

                    {it.hasOrder &&
                      order === OrderType.ASC &&
                      orderBy === it.key && (
                      <div className="ml-2">
                        <img
                          src={Images.ArrowUpIcon.default}
                          alt="arrow-up-icon"
                          className="w-4"
                        />
                      </div>
                    )}

                    {it.hasOrder &&
                      order === OrderType.DESC &&
                      orderBy === it.key && (
                      <div className="ml-2">
                        <img
                          src={Images.ArrowDownIcon.default}
                          alt="arrow-down-icon"
                          className="w-4"
                        />
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.items.map((item) => (
              <tr
                key={item.authorId}
                className="border-t border-[#EEF0FA] text-[14px] text-[#45464E] font-normal"
              >
                <td className="px-6 py-4">{item.name}</td>
                <td className="px-6 py-4">
                  <img
                    src={item.thumbnails}
                    alt="book image"
                    className="w-[32px] h-[32px] rounded-[5px]"
                  />
                </td>
                <td className="px-6 py-4">
                  {item.retailRevenue?.toLocaleString("de-DE") ?? 0}đ
                </td>
                <td className="px-6 py-4">
                  {item.retailRevenueAllTime?.toLocaleString("de-DE") ?? 0}đ
                </td>
                <td className="px-6 py-4">
                  {item.vipRevenue?.toLocaleString("de-DE") ?? 0}đ
                </td>
                <td className="px-6 py-4">
                  {item.vipRevenueAllTime?.toLocaleString("de-DE") ?? 0}đ
                </td>
                <td className="px-6 py-4">
                  {item.paidAmount?.toLocaleString("de-DE") ?? 0}đ
                </td>
                <td className="px-6 py-4">
                  {item.remainAmount?.toLocaleString("de-DE") ?? 0}đ
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PaginationMui
        currentPage={meta?.pageNum ?? 1}
        totalPages={meta?.totalPage ?? 1}
        sizePage={meta?.pageSize ?? 1}
        total={meta?.total ?? 0}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
};

export default memo(AuthorPaymentInformation);
